import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { getAccessToken, getActiveCompanyId, buildQuery, unwrapList, unwrapRecordOrObject, unwrapError, getTransportError } from './apiUtils';

/** Cuenta contable propia de un proveedor en la sociedad activa (subcuenta 400NNNN o 410NNNN). */
export interface SupplierAccount {
  supplierId: string;
  accountId: string;
  code: string;
  name: string;
}

/** 400 = Proveedores, 410 = Acreedores por prestaciones de servicios (PGC). */
export type SupplierAccountFamily = '400' | '410';

const mapSupplierAccount = (raw: any): SupplierAccount => ({
  supplierId: String(raw.supplier_id),
  accountId:  String(raw.account_id),
  code:       String(raw.code ?? ''),
  name:       raw.name || ''
});

/** Cuentas propias de los proveedores de la sociedad activa, indexadas por id de proveedor. */
export const listSupplierAccounts = async (): Promise<Record<string, SupplierAccount>> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/supplier_accounts_list${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);

  const out: Record<string, SupplierAccount> = {};
  unwrapList(res).map(mapSupplierAccount).forEach(a => { out[a.supplierId] = a; });
  return out;
};

/**
 * Asocia al proveedor una cuenta 400NNNN/410NNNN que ya existe en el plan (supplier_accounts_set).
 * Con accountId vacío le quita la asociación (la cuenta sigue en el plan, libre para otro proveedor).
 * Con accountId 'new' crea la siguiente cuenta libre de la familia indicada y se la asocia (o devuelve la que ya tiene).
 */
export const setSupplierAccount = async (
  supplierId: string,
  accountId: string,
  family: SupplierAccountFamily = '400'
): Promise<SupplierAccount | null> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/supplier_accounts_set${buildQuery({
    access_token: token,
    company_id:   getActiveCompanyId(),
    supplier_id:  supplierId,
    account_id:   accountId,
    family:       accountId === 'new' ? family : undefined
  })}`);
  const out = unwrapRecordOrObject(res);
  if (!out || (!out.message && !out.account_id)) throw new Error(unwrapError(res, 'No se pudo asociar la cuenta al proveedor.'));
  return out.account_id ? mapSupplierAccount({ ...out, supplier_id: out.supplier_id ?? supplierId }) : null;
};

/** La cuenta propia del proveedor; si todavía no la tiene, la crea en la familia indicada. */
export const ensureSupplierAccount = async (supplierId: string, family: SupplierAccountFamily = '400'): Promise<SupplierAccount> => {
  const account = await setSupplierAccount(supplierId, 'new', family);
  if (!account) throw new Error('No se pudo crear la cuenta del proveedor.');
  return account;
};
